import { useState } from 'react';
import { WifiOff, RefreshCw, Signal } from 'lucide-react';
import { useOfflineStatus } from '@/hooks/useOfflineStatus';

/**
 * OfflineIndicator — badge de estado de red en la topbar. Tres modos:
 *
 *   1. Offline (sin conexión)  →  badge ámbar `WifiOff` + botón "Reintentar".
 *   2. Online + red lenta (2G) →  badge azul claro `Signal` "Conexión lenta".
 *   3. Online + red normal     →  nada (evita clutter en la topbar).
 *
 * La detección de red lenta usa `navigator.connection.effectiveType` (Network
 * Information API). Safari no la soporta y devuelve `null`, por lo que en iPad
 * este badge nunca se mostrará — degradación limpia.
 */
export default function OfflineIndicator() {
  const { isOffline, isSlow, effectiveType } = useOfflineStatus();
  const [checking, setChecking] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleRetry = async (e) => {
    e?.preventDefault?.();
    if (checking) return;
    setChecking(true);
    setFailed(false);
    try {
      const resp = await fetch('/api/health?nocache=1', {
        method: 'GET',
        cache: 'no-store',
        credentials: 'same-origin',
      });
      if (resp && resp.ok) {
        window.location.reload();
        return;
      }
      throw new Error(`bad status ${resp?.status}`);
    } catch (_err) {
      setFailed(true);
      setChecking(false);
      setTimeout(() => setFailed(false), 900);
    }
  };

  // Modo 1: offline
  if (isOffline) {
    return (
      <div
        data-testid="offline-indicator"
        role="status"
        aria-live="polite"
        title="Se perdió la conexión a internet. Estás viendo los últimos datos que se cargaron online."
        className={
          'inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 pl-2.5 pr-1 py-1 text-xs font-medium text-amber-800 shadow-sm ' +
          (failed ? 'animate-shake' : 'animate-pulse')
        }
      >
        <WifiOff size={14} className="text-amber-700" aria-hidden="true" />
        <span className="hidden sm:inline">Sin conexión</span>
        <span className="hidden md:inline text-amber-600 font-normal">· datos del último uso</span>
        <button
          type="button"
          onClick={handleRetry}
          disabled={checking}
          data-testid="offline-retry-button"
          aria-label="Reintentar conexión"
          title={failed ? 'Aún sin conexión. Reintenta en unos segundos.' : 'Reintentar conexión'}
          className={
            'ml-1 inline-flex items-center gap-1 rounded-full bg-white/70 hover:bg-white px-2 py-0.5 text-[11px] font-semibold text-amber-800 border border-amber-200 transition-colors ' +
            (checking ? 'opacity-70 cursor-wait' : 'cursor-pointer')
          }
        >
          <RefreshCw size={11} className={checking ? 'animate-spin' : ''} aria-hidden="true" />
          <span>{checking ? 'Comprobando…' : failed ? 'Reintenta' : 'Reintentar'}</span>
        </button>
      </div>
    );
  }

  // Modo 2: online pero red lenta (solo si el API expone effectiveType 2g/slow-2g)
  if (isSlow) {
    return (
      <div
        data-testid="slow-connection-indicator"
        role="status"
        aria-live="polite"
        title={`Red móvil lenta (${effectiveType?.toUpperCase() ?? 'desconocida'}). Las cargas pueden tardar un poco más de lo habitual.`}
        className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-800 shadow-sm"
      >
        <Signal size={14} className="text-sky-600" aria-hidden="true" />
        <span className="hidden sm:inline">Conexión lenta</span>
        <span className="hidden md:inline text-sky-600 font-normal uppercase">· {effectiveType}</span>
      </div>
    );
  }

  // Modo 3: online + red normal → nada
  return null;
}
